import moment from 'moment';
import { Box, Chip, Stack } from '@mui/material';
import { Control, UseFormSetValue } from 'react-hook-form';
import { DateField } from 'components/form/rhf/DateField';
import { SmartIntakeFiltersForm, QuickDatePreset } from './SmartIntakeTypes';

const MAX_DATE_RANGE_DAYS = 31;

const QUICK_DATE_PRESETS: { label: string; preset: QuickDatePreset }[] = [
  { label: 'Today', preset: 'today' },
  { label: 'Yesterday', preset: 'yesterday' },
  { label: 'Last week', preset: 'lastWeek' },
  { label: 'Last month', preset: 'lastMonth' },
];

export function resolveQuickDatePreset(preset: QuickDatePreset): { from: moment.Moment; to: moment.Moment } {
  switch (preset) {
    case 'today':
      return { from: moment().startOf('day'), to: moment().endOf('day') };
    case 'yesterday':
      return { from: moment().subtract(1, 'day').startOf('day'), to: moment().subtract(1, 'day').endOf('day') };
    case 'lastWeek':
      return { from: moment().subtract(1, 'week').startOf('week'), to: moment().subtract(1, 'week').endOf('week') };
    case 'lastMonth':
      return { from: moment().subtract(1, 'month').startOf('month'), to: moment().subtract(1, 'month').endOf('month') };
  }
}

interface Props {
  control: Control<SmartIntakeFiltersForm>;
  setValue: UseFormSetValue<SmartIntakeFiltersForm>;
  activePreset: QuickDatePreset | null;
  onPresetChange: (preset: QuickDatePreset) => void;
  from: moment.Moment | null;
  to: moment.Moment | null;
}

export const SmartIntakeDateRangeFilter = ({ control, setValue, activePreset, onPresetChange, from, to }: Props) => {
  const maxDateForTo = from ? moment(from).add(MAX_DATE_RANGE_DAYS - 1, 'days') : undefined;
  const minDateForFrom = to ? moment(to).subtract(MAX_DATE_RANGE_DAYS - 1, 'days') : undefined;

  const handlePreset = (preset: QuickDatePreset) => {
    const { from, to } = resolveQuickDatePreset(preset);
    setValue('from', from);
    setValue('to', to);
    onPresetChange(preset);
  };

  return (
    <Stack spacing={0.75} sx={{ width: 360, flexShrink: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <DateField
          name="from"
          label="From"
          control={control}
          slotProps={{ textField: { size: 'small' } }}
          minDate={minDateForFrom}
          extraAction={value => {
            if (value && to && moment(to).diff(value, 'days') >= MAX_DATE_RANGE_DAYS) {
              setValue(
                'to',
                moment(value)
                  .add(MAX_DATE_RANGE_DAYS - 1, 'days')
                  .endOf('day')
              );
            }
          }}
        />
        <Box sx={{ color: 'text.secondary', flexShrink: 0 }}>–</Box>
        <DateField
          name="to"
          label="To"
          control={control}
          slotProps={{ textField: { size: 'small' } }}
          maxDate={maxDateForTo}
          extraAction={value => {
            if (value && from && moment(value).diff(from, 'days') >= MAX_DATE_RANGE_DAYS) {
              setValue(
                'from',
                moment(value)
                  .subtract(MAX_DATE_RANGE_DAYS - 1, 'days')
                  .startOf('day')
              );
            }
          }}
        />
      </Stack>

      <Stack direction="row" spacing={0.75}>
        {QUICK_DATE_PRESETS.map(({ label, preset }) => (
          <Chip
            key={preset}
            label={label}
            size="small"
            variant={activePreset === preset ? 'filled' : 'outlined'}
            color={activePreset === preset ? 'primary' : 'default'}
            onClick={() => handlePreset(preset)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>
    </Stack>
  );
};
